import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaRegTrashAlt } from 'react-icons/fa';
import { InferType, number, object, string } from 'yup';
import dayjs from 'dayjs';

import { PriceReadModel } from '@client/api';
import {
  Button,
  DatepickerV2,
  FormField,
  InfoTooltip,
  Input,
  isDefinedString,
  TableCell,
  TableRow,
  Text,
  toIntervalString,
  toMoneyStringOrUndefined,
} from '@client/toolkit';

const schema = object({
  id: string().nullable(),
  priceId: string().required(),
  quantity: number().positive().required(),
  interval: object({
    start: string().nullable(),
    end: string().nullable(),
  }).nullable(),
  overwrittenUnitAmount: object({
    amount: number().nullable(),
    currency: object({
      code: string().nullable(),
    }).nullable(),
  }).nullable(),
});

type FormValues = InferType<typeof schema>;

export const agreementProductSchema = schema;
export type AgreementProductFormData = FormValues;

interface AgreementProductItemFormProps {
  fieldIndex: number;
  productPrice?: PriceReadModel;
  allPrices?: PriceReadModel[];
  isFetching?: boolean;
  isInvoiced?: boolean;
  onRemove?: (fieldIndex: number) => void;
  defaultCurrencyCode?: string;
  readOnly?: boolean;
}

export function AgreementProductItemForm({
  fieldIndex,
  productPrice: initialPrice,
  allPrices = [],
  isFetching,
  isInvoiced,
  onRemove,
  defaultCurrencyCode,
  readOnly = false,
}: AgreementProductItemFormProps) {
  const { t: tGlobal } = useTranslation('global');
  const { watch, setValue } = useFormContext();

  // Watch the current priceId to get the actual selected price
  const currentPriceId = watch(`products.${fieldIndex}.priceId`);

  // Get the current price from allPrices based on watched priceId
  const price = useMemo(() => {
    if (!currentPriceId) return initialPrice;
    return allPrices.find((p) => p.id === currentPriceId) ?? initialPrice;
  }, [currentPriceId, allPrices, initialPrice]);

  const quantity = watch(`products.${fieldIndex}.quantity`, 0);
  const overwrittenAmount = watch(`products.${fieldIndex}.overwrittenUnitAmount.amount`);
  const intervalStart = watch(`products.${fieldIndex}.interval.start`);
  const intervalEnd = watch(`products.${fieldIndex}.interval.end`);

  // Check if product is expired (stop date is in the past) - reactive to date changes
  const isExpired = useMemo(() => {
    if (!intervalEnd) return false;
    return dayjs(intervalEnd).isBefore(dayjs(), 'day');
  }, [intervalEnd]);

  const effectivePrice = useMemo(() => {
    if (overwrittenAmount != null && overwrittenAmount !== '') {
      return { amount: Number(overwrittenAmount), currency: price?.unitAmount?.currency };
    }
    return price?.unitAmount;
  }, [overwrittenAmount, price?.unitAmount]);

  // Calculate number of months between start and end dates (inclusive - "til og med")
  const monthsInPeriod = useMemo(() => {
    if (!intervalStart || !intervalEnd) return null;
    const start = dayjs(intervalStart);
    const end = dayjs(intervalEnd).add(1, 'day'); // Add 1 day for inclusive end date
    if (!start.isValid() || !end.isValid()) return null;
    return Math.max(0, end.diff(start, 'month', true));
  }, [intervalStart, intervalEnd]);

  const totalAmountString = useMemo(() => {
    if (!effectivePrice || !quantity) return undefined;

    const monthlyTotal = effectivePrice.amount * quantity;
    let total = monthlyTotal;
    let suffix = '';

    // For recurring products
    if (price?.recurring) {
      // If we have a custom period less than 12 months, show period amount
      if (monthsInPeriod != null && monthsInPeriod < 12) {
        total = monthlyTotal * monthsInPeriod;
        suffix = ` (${Math.round(monthsInPeriod)} mnd)`;
      } else {
        // Otherwise show yearly amount (monthly * 12)
        total = monthlyTotal * 12;
        suffix = '/år';
      }
    }
    // For one-time products, show as is without suffix

    const moneyStr = toMoneyStringOrUndefined({ amount: total, currency: effectivePrice.currency ?? { code: 'NOK' } });
    return moneyStr ? `${moneyStr}${suffix}` : undefined;
  }, [effectivePrice, quantity, price?.recurring, monthsInPeriod]);

  const handleOverwrittenPriceChange = (value: string) => {
    if (value === '' || value == null) {
      setValue(`products.${fieldIndex}.overwrittenUnitAmount`, null, { shouldDirty: true });
    } else {
      setValue(
        `products.${fieldIndex}.overwrittenUnitAmount`,
        {
          amount: Number(value),
          currency: { code: price?.unitAmount?.currency?.code ?? defaultCurrencyCode ?? 'NOK' },
        },
        { shouldDirty: true },
      );
    }
  };

  return (
    <TableRow verticalAlign="middle" className={isExpired ? 'opacity-50' : ''}>
      {/* Produkt */}
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!isDefinedString(price?.product?.name)}>
          <strong>{price?.product?.name}</strong>
          {isExpired && <span className="ml-2 text-xs text-text-secondary">(Utløpt)</span>}
        </Text>
      </TableCell>

      {/* Antall */}
      <TableCell loading={isFetching}>
        <FormField
          name={`products.${fieldIndex}.quantity`}
          defaultValue={''}
          render={({ field }) => <Input type="number" size="medium" {...field} disabled={readOnly} />}
        />
      </TableCell>

      {/* Start */}
      <TableCell loading={isFetching}>
        <FormField
          name={`products.${fieldIndex}.interval.start`}
          render={({ field: { value, onChange, ref, ...field } }) => (
            <DatepickerV2
              value={value ? dayjs(value) : undefined}
              onChange={(date) => onChange(date?.format('YYYY-MM-DD') ?? null)}
              className="w-full"
              {...field}
              disabled={readOnly}
            />
          )}
        />
      </TableCell>

      {/* Stopp */}
      <TableCell loading={isFetching}>
        <FormField
          name={`products.${fieldIndex}.interval.end`}
          render={({ field: { value, onChange, ref, ...field } }) => (
            <DatepickerV2
              value={value ? dayjs(value) : undefined}
              onChange={(date) => onChange(date?.format('YYYY-MM-DD') ?? null)}
              className="w-full"
              {...field}
              disabled={readOnly}
            />
          )}
        />
      </TableCell>

      {/* Produktpris */}
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!price?.unitAmount}>
          {toMoneyStringOrUndefined(price?.unitAmount)}
        </Text>
      </TableCell>

      {/* Overstyrt produktpris */}
      <TableCell loading={isFetching}>
        <Input
          type="number"
          size="medium"
          placeholder="-"
          value={overwrittenAmount ?? ''}
          disabled={readOnly}
          onChange={(e) => handleOverwrittenPriceChange(e.target.value)}
        />
      </TableCell>

      {/* Periode */}
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!price}>
          {price?.recurring === false ? 'Engangs' : toIntervalString(price?.interval)}
        </Text>
      </TableCell>

      {/* Fakturert */}
      <TableCell loading={isFetching}>
        {price?.recurring === false && isInvoiced != null ? (
          <Text as="div">{isInvoiced ? tGlobal('yes') : tGlobal('no')}</Text>
        ) : (
          <Text as="div">-</Text>
        )}
      </TableCell>

      {/* Totalpris + Delete */}
      <TableCell align="right" loading={isFetching} className="flex items-center justify-end">
        <div className="text-primary whitespace-nowrap">
          <Text as="div" isEmpty={!isDefinedString(totalAmountString)}>
            {totalAmountString}
          </Text>
        </div>
        <InfoTooltip message="Kan ikke slettes pga. aktiv avtale" disabled={typeof onRemove === 'function'}>
          <Button
            className="ml-2 text-brand-primary bg-surface-elevated"
            size="xs"
            color="custom"
            disabled={typeof onRemove !== 'function' || isFetching}
            onClick={() => {
              if (typeof onRemove !== 'function') {
                return;
              }
              onRemove(fieldIndex);
            }}
          >
            <FaRegTrashAlt />
          </Button>
        </InfoTooltip>
      </TableCell>
    </TableRow>
  );
}
